import { useMemo, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';
import {
  useScheduledActivitiesBuilder,
  CUSTOM_FIELD_ITEM_ID,
} from 'components/Wizards/Dashlet/hooks/useScheduledActivitiesBuilder';
import useMetadataKey from 'components/Wizards/shared/hooks/useMetadataKey';
import EditableTextEllipsisWithTooltip from './EditableTextEllipsisWithTooltip';

import BaseOption from 'components/MultiColumnBuilder/components/BaseOption';
import IconButtonMenu from 'components/Kizen/IconButtonMenu';
import Icon from 'components/Kizen/Icon';
import MultiColumnBuilder from 'components/MultiColumnBuilder';
import InputControl from 'components/Inputs/InputControl';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';

import { KizenTypography } from '__app/typography';
import { colorsButton } from 'app/colors';
import { fontWeights } from 'app/typography';

import { ColumnsHeaderPreview } from 'components/Wizards/shared/components/TableBuilder/components/ColumnsHeaderPreview';
import { CustomFieldModal } from 'pages/Common/components/ColumnModal/CustomFieldModal';
import {
  LeftColumnWrapper,
  PivotDropPlaceholder,
} from '__components/Wizards/RTDV/sections/ValuesAndConstraints/styles';
import {
  NoItemsWrapper,
  StyledDragItem,
} from '__components/MultiColumnBuilder/components/BaseOption/styles';
import { getFieldsDescription } from 'components/Fields/FieldInput/helpers';

export const MultiColumnBuilderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 420px;
  p {
    margin-bottom: 0 !important;
  }
`;

export const ColumnWrapper = styled.div`
  width: 1020px;
  min-height: 420px;
`;

export const StyledInputControl = styled(InputControl)`
  margin-top: 15px;
  p {
    margin-bottom: 0 !important;
  }
`;

export const TextWrapper = styled.div`
  max-width: ${({ showDropdown }) => (showDropdown ? '290' : '400')}px;
`;

export const StyledTextEllipsisWithTooltip = styled(TextEllipsisWithTooltip)`
  flex-grow: 1;
  margin-right: 20px;
  width: 0;

  ${({ isDynamic }) =>
    isDynamic
      ? css`
          color: var(--color-button-primary-default);
        `
      : ''}
`;

const getFalse = () => false;

export const ScheduledActivitiesColumnBuilder = ({
  scrollActvitiyBuilderIntoView,
  columns,
  defaultColumns,
  scheduledActivitiesConfig,
  displayHeader,
  onChange,
  displayDefaultColumns,
  disabledNameField,
  isGropedOptions,
}) => {
  const { t } = useTranslation();

  const { builderProps, showModal, setShowModal, customFieldItem } =
    useScheduledActivitiesBuilder({
      settings: columns,
      defaultColumns: defaultColumns,
      displayDefaultColumns,
      orderSorting: columns.map(({ id }) => id),
      disabledNameField,
      isGropedOptions,
      t,
    });

  const memoizedScheduledActivitiesConfig = useMemo(
    () => ({
      ...scheduledActivitiesConfig,
      columns: builderProps.rightColumnItems.map((item) => {
        if (item.localizedLabel) {
          delete item.localizedLabel;
        }
        return item;
      }),
    }),
    [scheduledActivitiesConfig, builderProps.rightColumnItems]
  );

  const memoizedScheduledActivitiesConfigRef = useRef(
    memoizedScheduledActivitiesConfig
  );
  useEffect(() => {
    if (
      onChange &&
      !isEqual(
        memoizedScheduledActivitiesConfigRef.current,
        memoizedScheduledActivitiesConfig
      )
    ) {
      onChange(memoizedScheduledActivitiesConfig);
      memoizedScheduledActivitiesConfigRef.current =
        memoizedScheduledActivitiesConfig;
    }
  }, [memoizedScheduledActivitiesConfig, onChange]);

  useMetadataKey(
    'scheduledActivitiesConfig',
    memoizedScheduledActivitiesConfig
  );

  const leftColumnProps = useMemo(
    () => ({
      ...builderProps.leftColumnProps,
      options: [
        builderProps.leftColumnProps.options[0]?.fieldId !==
          CUSTOM_FIELD_ITEM_ID && customFieldItem,
        ...builderProps.leftColumnProps.options,
      ]
        .filter(Boolean)
        .filter(({ fieldId }) => {
          return !fieldId || fieldId === CUSTOM_FIELD_ITEM_ID;
        })
        .map((option) => ({ ...option, label: option.displayName })),
    }),
    [builderProps.leftColumnProps, customFieldItem]
  );

  const { setOptionsSearchTerm, rightColumnProps } = useMemo(
    () => builderProps,
    [builderProps]
  );

  const customFieldMenuOptions = useMemo(
    () => [
      {
        value: 'remove',
        label: t('Remove Column'),
      },
      {
        value: 'edit',
        label: t('Edit Options'),
      },
    ],
    [t]
  );

  const onSelectOption = useCallback(
    ({ value }, removeItem, element, index) => {
      if (value === 'remove') {
        removeItem(index);
      } else if (value === 'edit') {
        setShowModal({
          index,
          ...element,
        });
      }
    },
    [setShowModal]
  );

  const additionalActions = useCallback(
    ({
      swapItemColumn,
      removeItem,
      index,
      element,
      editValue,
      childIndex,
      section,
    }) => {
      const showEdit = section === 'right-column' && element?.fieldId;
      const showDelete =
        section === 'right-column' && !showEdit && !element?.disabled;
      return (
        <>
          {!element?.disabled &&
            swapItemColumn &&
            !element.restricted &&
            showDelete && (
              <div>
                <Icon
                  className="DeleteAction"
                  icon="delete"
                  onClick={() => {
                    editValue(index, childIndex, 'label', element.displayName);
                    swapItemColumn(index, section);
                  }}
                />
              </div>
            )}
          {showEdit && (
            <IconButtonMenu
              title={t('Edit Settings')}
              position="left"
              sizing="dense"
              color={colorsButton.iconGray}
              options={customFieldMenuOptions}
              onChange={(selected) => {
                onSelectOption(
                  { value: selected.value },
                  removeItem,
                  element,
                  index
                );
              }}
              restrictCloseHandler
              overlay
            >
              <Icon icon="three-dot" />
            </IconButtonMenu>
          )}
        </>
      );
    },
    [customFieldMenuOptions, onSelectOption, t]
  );

  const handleCustomFieldModalConfirm = useCallback(
    async ({ index, label, fieldId, objectId, displayName }) => {
      const { editValue } = rightColumnProps;
      editValue(index, undefined, 'id', uuidv4());
      editValue(index, undefined, 'label', label);
      editValue(index, undefined, 'objectId', objectId);
      editValue(index, undefined, 'fieldId', fieldId);
      editValue(index, undefined, 'displayName', displayName);
    },
    [rightColumnProps]
  );

  const onHideModal = useCallback(
    ({ reset = false } = {}) => {
      if (reset) {
        rightColumnProps.removeItem(showModal.index);
      }
      setShowModal(null);
    },
    [rightColumnProps, setShowModal, showModal]
  );

  const scrollColumnEditorToView = useCallback(
    (el) => {
      setTimeout(() => {
        scrollActvitiyBuilderIntoView &&
          el?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    },
    [scrollActvitiyBuilderIntoView]
  );

  return (
    <>
      <ColumnWrapper ref={scrollColumnEditorToView}>
        {displayHeader ? (
          <KizenTypography type="subheader">
            {t('Choose Columns to Display')}
          </KizenTypography>
        ) : null}
        <StyledInputControl label={t('Column Preview')}>
          <ColumnsHeaderPreview columns={rightColumnProps.options} />
        </StyledInputControl>
        <MultiColumnBuilderWrapper>
          <MultiColumnBuilder
            {...builderProps}
            searchable
            testId="edit-columns-builder"
            weight={fontWeights.regular}
            leftColumnProps={leftColumnProps}
            leftHeaderText={t('Available Columns (Drag to Add)')}
            rightHeaderText={t('Active Table Columns')}
            searchPlaceholder={t('Find Options')}
            onChangeSearchTerm={setOptionsSearchTerm}
            disabledDropZoneText={false}
            rollBackDrop={false}
            renderRightEmpty={{
              onDropLabel: t('Place Here'),
              noItems: t(
                'No Columns Currently Selected (Drag and Drop to Add)'
              ),
            }}
            renderLeftEmpty={({ dropZone, id }) => {
              if (dropZone?.sectionId === id) {
                return (
                  <PivotDropPlaceholder isChildDragging={false}>
                    <StyledDragItem className={`MenuItemWrapper-Child-empty`}>
                      <KizenTypography>{t('Drop to Remove')}</KizenTypography>
                    </StyledDragItem>
                  </PivotDropPlaceholder>
                );
              }
              return (
                <LeftColumnWrapper>
                  <NoItemsWrapper>
                    <KizenTypography>{t('No Options Found')}</KizenTypography>
                  </NoItemsWrapper>
                </LeftColumnWrapper>
              );
            }}
            s
            renderOption={({ element, section, index, ...providedProps }) => {
              return (
                <BaseOption
                  data-qa={element?.label}
                  iconVisible={getFalse}
                  iconEditable={getFalse}
                  colorEditable={getFalse}
                  element={element}
                  section={section}
                  additionalActions={additionalActions}
                  displayAddToBottomRightButton={section === 'left-column'}
                  onMoveToBottomRight={
                    index === 0
                      ? () =>
                          providedProps?.copyItemToNewColumn(
                            0,
                            section,
                            customFieldItem
                          )
                      : () =>
                          providedProps?.swapItemColumn(
                            index - 1,
                            section,
                            false,
                            true,
                            element?.id
                          )
                  }
                  index={index}
                  {...providedProps}
                >
                  {({ element, editValue, index, childIndex, ...rest }) => {
                    const isDynamic = element?.fieldId === CUSTOM_FIELD_ITEM_ID;
                    const canEdit = section === 'right-column';
                    const displayName = canEdit
                      ? element?.label
                      : element?.displayName;

                    const { withNameDescription } = getFieldsDescription({
                      displayName: displayName,
                      ...element,
                    });
                    return canEdit ? (
                      <TextWrapper>
                        <EditableTextEllipsisWithTooltip
                          description={withNameDescription}
                          value={element.label}
                          sizing="dense"
                          onChange={(v) => {
                            editValue(index, childIndex, 'label', v);
                          }}
                          placeholder={t('Enter Display Name')}
                          onBlur={(e) => {
                            if (!e.target.value) {
                              editValue(
                                index,
                                childIndex,
                                'label',
                                element.displayName
                              );
                            }
                          }}
                          {...rest}
                        />
                      </TextWrapper>
                    ) : (
                      <StyledTextEllipsisWithTooltip
                        isDynamic={isDynamic}
                        description={withNameDescription}
                      >
                        {canEdit ? element.label : element.displayName}
                      </StyledTextEllipsisWithTooltip>
                    );
                  }}
                </BaseOption>
              );
            }}
          />
        </MultiColumnBuilderWrapper>
      </ColumnWrapper>
      {showModal ? (
        <CustomFieldModal
          {...showModal}
          onConfirm={handleCustomFieldModalConfirm}
          onHide={onHideModal}
          editValue={rightColumnProps.editValue}
        />
      ) : null}
    </>
  );
};
